import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { DefaultButton, PrimaryButton, IButtonStyles } from '@fluentui/react/lib/Button';
import {
    skittleDialogTitlePreviewPremium,
    skittleGoPremiumButton,
    skittleDialogTryItNowButton,
} from './SkittleCallout.locstring.json';
import loc, { format } from 'owa-localize';
import { PremiumPreviewData } from '../store/schema/PremiumPreviewData';
import { getTextStringSkittle } from '../utils/getTextStringSkittle';
import { openGoPremiumUrl } from '../utils/openGoPremiumUrl';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import { NUMBER_OF_DAYS_FREE_TRIAL, NUMBER_OF_SUGGESTIONS } from '../constants';
import { logOnUpsellClicked } from '../utils/telemetry/logOnUpsellClicked';
import { logTryItNowButtonClicked } from '../utils/telemetry/logTryItNowButtonClicked';
import classNames from 'classnames';

const styles = require('./UpsellSkittle.scss');
const EditorCalloutImage = require('../svg/EditorUpsell.svg');

export interface UpsellPremiumPreviewBaseProps {
    onTryItNowClick: (numerOfDaysLeft: number, premiumSuggestionsCount: number) => void;
    onHoverPremiumPreview: ((previewActive: boolean) => void) | null;
    premiumPreviewData: PremiumPreviewData;
}

const UpsellPremiumPreviewBase = observer(function UpsellPremiumPreviewBase(
    props: UpsellPremiumPreviewBaseProps
) {
    // This component rendered after clicking on 'try now'
    // we should log once on each render
    React.useEffect(() => {
        logTryItNowButtonClicked(
            props.premiumPreviewData.premiumPreviewSource,
            true /* hasPremiumPreviewRenderedAfterClickTryNow */
        );
    }, []);

    const onGoPremiumClick = React.useCallback(() => {
        openGoPremiumUrl(
            props.premiumPreviewData.premiumPreviewType,
            props.premiumPreviewData.premiumPreviewStatus,
            'Callout'
        );
        logOnUpsellClicked('PremiumPreview', props.premiumPreviewData, '' /* type */);
    }, [
        props.premiumPreviewData.premiumPreviewType,
        props.premiumPreviewData.premiumPreviewStatus,
        props.premiumPreviewData,
    ]);

    const onTryItNowClicked = React.useCallback(() => {
        props.onTryItNowClick(NUMBER_OF_DAYS_FREE_TRIAL, NUMBER_OF_SUGGESTIONS);
    }, []);

    const { stringToShowOnPremiumPreviewCallout, numberToShow } = getTextStringSkittle(
        props.premiumPreviewData,
        NUMBER_OF_DAYS_FREE_TRIAL,
        NUMBER_OF_SUGGESTIONS
    );
    const stringToShow: string = format(loc(stringToShowOnPremiumPreviewCallout), numberToShow);

    const buttonStyles: IButtonStyles = {
        flexContainer: styles.goPremiumButoon,
    };

    const onMouseEnter = React.useCallback(() => {
        props.onHoverPremiumPreview?.(true /* previewActive */);
    }, [props.onHoverPremiumPreview]);

    const onMouseLeave = React.useCallback(() => {
        props.onHoverPremiumPreview?.(false /* previewActive */);
    }, [props.onHoverPremiumPreview]);
    const isDialogSource = props.premiumPreviewData.premiumPreviewSource !== 'skittle';

    const imageStyle = isDialogSource
        ? styles.editorPremPrevImageDialog
        : styles.editorPremPrevImage;
    return (
        <div
            className={styles.premPrevSkittleContainer}
            aria-label={skittleDialogTitlePreviewPremium}
            onMouseLeave={onMouseLeave}
            onMouseEnter={onMouseEnter}>
            <div
                className={classNames(
                    styles.innerContainerPP,
                    !isDialogSource && styles.calloutPP
                )}>
                <div className={imageStyle}>
                    <img src={EditorCalloutImage} className={styles.editorSVG} />
                </div>
                <span className={styles.premPrevTitle}>
                    {loc(skittleDialogTitlePreviewPremium)}
                </span>
                <span className={styles.editorPremPrevText}>{stringToShow}</span>
                {!isDialogSource && (
                    <div className={styles.premPrevbuttonsContainer} aria-label={stringToShow}>
                        <PrimaryButton
                            onClick={onTryItNowClicked}
                            text={loc(skittleDialogTryItNowButton)}
                            className={classNames(styles.tryItNowButoon, styles.goPremiumButoon)}
                        />
                        <DefaultButton
                            onClick={onGoPremiumClick}
                            styles={buttonStyles}
                            text={loc(skittleGoPremiumButton)}
                            className={styles.goPremiumButoon}>
                            <Icon
                                iconName={ControlIcons.Diamond}
                                className={styles.premiumPreviewDiamonIcon}
                            />
                        </DefaultButton>
                    </div>
                )}
            </div>
        </div>
    );
});

export { UpsellPremiumPreviewBase };
